import React, { useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';

/**
 * Individual fee row component
 */
const FeeRow = React.memo(
    ({ fee, idx, onUpdate, onRemove, onOpenTranslations, venueId, error }) => {
        const { t } = useTranslation();

        const getErrorMessage = (field) => {
            if (!error) return '';
            if (typeof error === 'string') return error;
            if (typeof error === 'object' && error[field]) return error[field];
            return '';
        };

        return (
            <TableRow hover>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('shows.columns.feesAttributes.name')}
                            value={fee.name}
                            onChange={(ev) => onUpdate(fee.id, 'name', ev.target.value)}
                            error={Boolean(getErrorMessage('name'))}
                            helperText={getErrorMessage('name')}
                            variant="standard"
                        />
                        <Tooltip title={t('common.translations')}>
                            <IconButton
                                size="small"
                                onClick={() => onOpenTranslations(fee.id, idx)}
                                disabled={!venueId}
                            >
                                <LanguageIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                    <Checkbox
                        checked={Boolean(fee.is_tax)}
                        onChange={(ev) => onUpdate(fee.id, 'is_tax', ev.target.checked)}
                    />
                </TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                    <Checkbox
                        checked={Boolean(fee.online_only)}
                        onChange={(ev) => onUpdate(fee.id, 'online_only', ev.target.checked)}
                    />
                </TableCell>
                <TableCell sx={{ width: 150 }}>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="0"
                        value={fee.percentage}
                        onChange={(ev) => onUpdate(fee.id, 'percentage', ev.target.value)}
                        error={Boolean(getErrorMessage('percentage') || getErrorMessage(null))}
                        helperText={getErrorMessage('percentage') || getErrorMessage(null)}
                        variant="standard"
                    />
                </TableCell>
                <TableCell sx={{ width: 150 }}>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="0"
                        value={fee.usd}
                        onChange={(ev) => onUpdate(fee.id, 'usd', ev.target.value)}
                        error={Boolean(getErrorMessage('usd') || getErrorMessage(null))}
                        helperText={getErrorMessage('usd') || getErrorMessage(null)}
                        variant="standard"
                    />
                </TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                    <Tooltip title={t('common.delete')}>
                        <IconButton size="small" onClick={() => onRemove(fee.id)} color="error">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        );
    },
);

FeeRow.displayName = 'FeeRow';

/**
 * FeesManager component - manages fees for a show
 */
const FeesManager = React.memo(({ fees = [], onChange, venueId, errors = [] }) => {
    const { t } = useTranslation();
    const { showModal } = useModals();

    const handleAddFee = useCallback(() => {
        const newFee = {
            id: `${Math.random()}`.substring(2),
            name: '',
            nameI18n: {},
            percentage: '0',
            usd: '0',
            is_tax: false,
            online_only: false,
        };
        onChange([...fees, newFee]);
    }, [fees, onChange]);

    const handleRemoveFee = useCallback(
        (feeId) => {
            onChange(fees.filter((fee) => fee.id !== feeId));
        },
        [fees, onChange],
    );

    const handleUpdateFee = useCallback(
        (feeId, field, value) => {
            onChange(fees.map((fee) => (fee.id === feeId ? { ...fee, [field]: value } : fee)));
        },
        [fees, onChange],
    );

    const handleOpenTranslationModal = useCallback(
        (feeId, idx) => {
            const fee = fees[idx];
            showModal(MODAL_TYPE.TRANSLATIONS, {
                translations: fee.nameI18n,
                setTranslations: (nameI18n) => {
                    onChange(fees.map((f) => (f.id === feeId ? { ...f, nameI18n } : f)));
                },
                item: 'shows',
                column: 'name',
                intermediateColumn: 'columns.feesAttributes',
                venueId,
                originalString: fee.name,
            });
        },
        [fees, onChange, showModal, venueId],
    );

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'left' }}>
                {t('shows.feesTitle')}
            </Typography>

            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                {t('shows.feesInfo')}
            </Alert>

            <TableContainer>
                <Table size="small" sx={{ minWidth: 760 }}>
                    {/* Name column has no fixed width; it absorbs the extra so its
                        field + globe stay usable while the rest keep fixed widths. */}
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('shows.columns.feesAttributes.name')}</TableCell>
                            <TableCell align="center">
                                {t('shows.columns.feesAttributes.isTax')}
                            </TableCell>
                            <TableCell align="center">
                                {t('shows.columns.feesAttributes.onlineOnly')}
                            </TableCell>
                            <TableCell>{t('shows.columns.feesAttributes.percentage')}</TableCell>
                            <TableCell>{t('shows.columns.feesAttributes.usd')}</TableCell>
                            <TableCell align="center">{t('common.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No fees or taxes configured
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            fees.map((fee, idx) => (
                                <FeeRow
                                    key={fee.id}
                                    fee={fee}
                                    idx={idx}
                                    onUpdate={handleUpdateFee}
                                    onRemove={handleRemoveFee}
                                    onOpenTranslations={handleOpenTranslationModal}
                                    venueId={venueId}
                                    error={errors[idx]}
                                />
                            ))
                        )}
                        <TableRow>
                            <TableCell colSpan={5} />
                            <TableCell align="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={handleAddFee}
                                    size="small"
                                >
                                    {t('common.add')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
});

FeesManager.displayName = 'FeesManager';

export default FeesManager;
